import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabase';

const actionLabels = {
  'auth.login': 'Logged in',
  'job.created': 'Created job',
  'job.updated': 'Updated job',
  'job.deleted': 'Deleted job',
  'property.created': 'Created property',
  'property.updated': 'Updated property',
  'property.deleted': 'Deleted property',
};

const actionColors = {
  'auth.login': 'bg-blue-100 text-blue-800',
  'job.created': 'bg-green-100 text-green-800',
  'job.updated': 'bg-yellow-100 text-yellow-800',
  'job.deleted': 'bg-red-100 text-red-800',
  'property.created': 'bg-green-100 text-green-800',
  'property.updated': 'bg-yellow-100 text-yellow-800',
  'property.deleted': 'bg-red-100 text-red-800',
};

function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let mounted = true;
    const fetchLogs = async () => {
      setLoading(true);
      let query = supabase
        .from('audit_logs')
        .select('id, action, user_email, record_type, details, created_at')
        .order('created_at', { ascending: false })
        .limit(100);

      if (filter !== 'all') {
        query = query.eq('record_type', filter);
      }

      const { data, error } = await query;
      if (!mounted) return;
      if (!error && data) setLogs(data);
      setLoading(false);
    };

    fetchLogs();
    return () => { mounted = false; };
  }, [filter]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatDetails = (details) => {
    if (!details) return '';
    const parts = [];
    if (details.property) parts.push(details.property);
    if (details.name) parts.push(details.name);
    if (details.scheduled_date) parts.push(details.scheduled_date);
    if (details.unit_number) parts.push(`Unit ${details.unit_number}`);
    return parts.join(' • ');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <main className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Activity Log</h1>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-coral"
          >
            <option value="all">All Activity</option>
            <option value="job">Jobs Only</option>
            <option value="property">Properties Only</option>
            <option value="auth">Logins Only</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading activity...</div>
        ) : logs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500">No activity logged yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {logs.map((log) => (
                <li key={log.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          actionColors[log.action] || 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {actionLabels[log.action] || log.action}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {log.user_email}
                        </p>
                        {log.details && (
                          <p className="text-sm text-gray-500">
                            {formatDetails(log.details)}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-gray-400">{formatDate(log.created_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}

export default ActivityLog;
